import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IApiService } from 'core/api/services/api.interface';
import { UserApiService } from 'features/user/api/api-tokens';
import { InboxPage } from '../notification.model';

/**
 * The in-app notification inbox.
 *
 * NOT named `NotificationService` — that name is already the app-wide transient
 * toast service (`shared/services/notification.service.ts`, ~40 call sites), and
 * shadowing it would be a very confusing import to debug.
 *
 * Bearer token and `ProfileKey` are attached by the interceptors, so every call
 * is scoped to the caller's active profile without passing it.
 */
@Injectable({ providedIn: 'root' })
export class NotificationInboxService {
  constructor(@Inject(UserApiService) protected apiService: IApiService) {}

  getInbox(limit = 20): Observable<InboxPage> {
    return this.apiService.get<InboxPage>('notificationList', {
      params: { unreadOnly: false, limit } as any,
    });
  }

  getUnreadCount(): Observable<{ unread: number }> {
    return this.apiService.get<{ unread: number }>('notificationUnreadCount');
  }

  markRead(notificationId: string): Observable<{ updated: number }> {
    // The body and the URL params both travel in the options object — this
    // ApiService takes `post(endpointName, options)`, not `post(name, body)`.
    return this.apiService.post<{ updated: number }>('notificationMarkRead', {
      body: {},
      urlParams: { notificationId },
    });
  }

  markAllRead(): Observable<{ updated: number }> {
    return this.apiService.post<{ updated: number }>('notificationMarkAllRead', { body: {} });
  }

  /**
   * Clear the caller's inbox. Per-user and non-destructive: the notifications
   * survive in the profile-wide log, they just stop appearing here.
   */
  dismissAll(): Observable<{ dismissed: number }> {
    return this.apiService.post<{ dismissed: number }>('notificationDismissAll', { body: {} });
  }
}
